import React, { useState } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Col, Container, Row } from "react-bootstrap";
import DataReactTable from "../../DataTable/components/DataReactTable";
import VehicleComponent from "../../../shared/components/table/components/VehicleComponent";
import RowOptions from "../../../shared/components/table/components/RowOptions";
import {
  AddFormButton,
  ColumnHeading,
} from "../../../shared/components/TableElements";
import { Link } from "react-router-dom";
import { NATEXT } from "../../../shared/constants";
import {
  formatNumber,
  getDistanceUnit,
  getListingDisplayDate,
} from "../../../shared/helpers";
import getUnixTime from "date-fns/getUnixTime";
import { LinkSpan } from "../../../shared/components/form/FormElements";
import ExplorePopup from "../../../shared/components/ExplorePopup";
import useFetchData from "../../../shared/custom-hooks/useFetchData";
import ProgressBar from "../../../shared/components/ProgressBar";
import getDefaultFilterData from "../../../shared/constants/getDefaultFilterData";
import { colorGray } from "../../../utils/palette";
import { useAuth } from "../../../contexts/AuthContext";

const MaintenanceRemindersTable = () => {
  const { t } = useTranslation("common");
  const { isViewOnly } = useAuth();

  const rowData = useSelector((state) => state.filter.rowData);

  const { distance } = useSelector((state) => state.units);

  const { minDate } = useSelector((state) => state.columns);

  const [showExplorePopup, setShowExplorePopup] = useState(false);

  useFetchData({
    startDate: 0,
    endDate: getUnixTime(new Date()),
    query: "get_all_service_reminder_data_v2",
    sortIndex: 3,
  });

  const { defaultContainsData, defaultRangeData, defaultStatusData } =
    getDefaultFilterData();

  const initialData = useMemo(
    () => ({
      // status: {
      //   column: "status",
      //   name: t("status"),
      //   ...defaultStatusData,
      // },
      task: {
        column: "task",
        name: t("task"),
        ...defaultContainsData,
      },
      daysRemaining: {
        column: "daysRemaining",
        name: t("days_remaining"),
        ...defaultRangeData,
      },
      odoRemaining: {
        column: "odoRemaining",
        name: t("odo_remaining"),
        ...defaultRangeData,
      },
      lastServiceOdo: {
        column: "lastServiceOdo",
        name: t("last_service_odo"),
        ...defaultRangeData,
      },
      lastServiceDate: {
        column: "lastServiceDate",
        name: t("last_service_date"),
        dateOption: "all_time",
        from: new Date(minDate * 1000),
        to: new Date(),
        isFiltered: false,
        mode: "custom",
      },
      dueOdo: {
        column: "dueOdo",
        name: t("due_odo"),
        ...defaultRangeData,
      },
      dueDate: {
        column: "dueDate",
        name: t("due_date"),
        dateOption: "all_time",
        from: new Date(minDate * 1000),
        to: new Date(),
        isFiltered: false,
        mode: "custom",
      },
    }),
    [minDate],
  );

  const columnHeaders = useMemo(
    () => [
      {
        id: 1,
        Header: t("asset"),
        accessor: "name",
        Cell: ({ row }) => (
          <VehicleComponent
            vehicleName={row.original.NAME}
            groupName="N/A"
            vehicleId={row.original.veh_id}
            vehicleImg={row.original.veh_img}
            orgId={row.original.org_id}
            assetType={row.original.asset_type}
          />
        ),
      },
      {
        id: 2,
        Header: t("task"),
        accessor: "service_task_name",
        accessorId: "task_name",
        Cell: ({ row }) => {
          const { service_task_name, recurring } = row.original;
          return (
            <div className="d-flex align-items-center gap-1">
              <p>{service_task_name}</p>
              {recurring === 0 && (
                <>
                  <p style={{ fontSize: "8px", color: colorGray }}>{"●"}</p>
                  <p style={{ color: colorGray }}>{t("one_time")}</p>
                </>
              )}
            </div>
          );
        },
      },
      {
        id: 3,
        Header: t("status"),
        accessor: "status",
        //disableSortBy: true,
        Cell: ({ row }) => {
          //console.log(row);
          const { due_days, last_date_of_service } = row.original;
          let due = last_date_of_service + Number(due_days) * 86400;
          let currentDate = Math.floor(Date.now() / 1000);
          let dueIn = ((due - currentDate) / (60 * 60 * 24)).toFixed(0);
          return (
            <StatusComponent
              due_days={row.original.due_days}
              due_odo={row.original.due_odo}
              distance={row.original["DUE ODO"] - row.original["MAX ODO"]}
              percentage={row.original.PERCENTAGE}
              days={dueIn}
              rowData={row.original}
            />
          );
        },
      },
      // {
      //   id: 4,
      //   Header: `${t("due_on")}`, //(${getDistanceUnit(distance)}/${t("date_tv")}),
      //   accessor: "due",
      //   disableSortBy: true,
      //   Cell: ({ row }) => {
      //     let dueDate = new Date(row.original.last_date_of_service * 1000);
      //     const dueDays = +row.original.due_days;
      //     let lastServiceDate = new Date(
      //       row.original.last_date_of_service * 1000
      //     );
      //     dueDate.setDate(lastServiceDate.getDate() + dueDays);
      //     dueDate =
      //       dueDate && dueDate instanceof Date && !isNaN(dueDate)
      //         ? getListingDisplayDate(dueDate / 1000) //dueDate.toLocaleDateString("en-US")
      //         : NATEXT;
      //     return (
      //       <DueComponent
      //         distance={row.original["DUE ODO"]}
      //         date={dueDate}
      //         due_days={row.original.due_days}
      //         due_odo={row.original.due_odo}
      //         rowData={row.original}
      //       />
      //     );
      //   },
      // },
      {
        id: 4,
        Header: `${t("due_on_odo")}`,
        accessor: "DUE ODO",
        //disableSortBy: true,
        Cell: ({ row }) => {
          const dueOdo = row.original["DUE ODO"];
          const { primary_meter, due_odo } = row.original;
          if (+dueOdo <= 0 || +due_odo <= 0) {
            return NATEXT;
          }
          let distanceUnit = useSelector((state) => state.units.distance);
          if (primary_meter === "Hours") {
            distanceUnit = "Hours";
          }
          const dueOdoVal = `${dueOdo} ${getDistanceUnit(distanceUnit)}`;
          return dueOdoVal;
        },
      },
      {
        id: 5,
        Header: `${t("due_on_date")}`,
        accessor: "DUE DATE",
        //disableSortBy: true,
        Cell: ({ row }) => {
          const dueDate = row.original["DUE DATE"];
          const { due_days } = row.original;
          if (+due_days <= 0) {
            return NATEXT;
          }
          return dueDate ? getListingDisplayDate(dueDate) : NATEXT;
        },
      },
      {
        id: 6,
        Header: `${t("last_serviced")}`, //(${getDistanceUnit(distance)}/${t("date_tv")}),
        accessor: "last_service",
        disableSortBy: true,
        Cell: ({ row }) => {
          const dueDate = new Date(row.original.last_date_of_service * 1000);
          const lastServiceDate =
            dueDate && dueDate instanceof Date && !isNaN(dueDate)
              ? getListingDisplayDate(dueDate / 1000) //dueDate.toLocaleDateString("en-US")
              : NATEXT;
          return (
            <LastServiceComponent
              distance={row.original.last_odo_of_service}
              date={lastServiceDate}
              rowData={row.original}
            />
          );
        },
      },
      {
        id: "edit",
        Header: "",
        accessor: "edit",
        accessorId: "edit",
        Cell: ({ row }) => <RowOptions rowData={row?.original} />,
        disableSortBy: true,
      },
      // {
      //   id: "edit",
      //   Header: "",
      //   accessor: "edit",
      //   accessorId: "edit",
      //   Cell: () => <RowOptions />,
      //   disableSortBy: true,
      // },
    ],
    [distance],
  );

  //Default Columns
  let count = 0;
  let defaultColumns = {};
  columnHeaders.forEach((column) => {
    defaultColumns[count] = {
      accessor: column.Header,
      header: column.Header,
    };
    count++;
  });

  const reactTableData = {
    tableHeaderData: columnHeaders,
    tableRowsData: rowData,
  };

  return (
    <>
      {showExplorePopup && (
        <ExplorePopup
          showPopup={showExplorePopup}
          setShowPopup={setShowExplorePopup}
          exploreLink={"https://embed.getsmartcue.com/IYBSWVIM"}
        />
      )}
      <Container>
        <Row className="mx-1 d-flex align-items-center justify-content-around p-0 m-0">
          {/* <Col
          sm={10}
          //className="d-flex justify-content-start align-items-center"
          >
          <ColumnHeading>{t("maintenance_reminders")}</ColumnHeading>
        </Col> */}
          <Col className="d-flex align-items-center gap-2">
            <ColumnHeading>{t("maintenance_reminders")}</ColumnHeading>

            <LinkSpan
              onClick={() => setShowExplorePopup(true)}
              style={{ alignSelf: "end" }}
            >
              {t("explore")}
            </LinkSpan>
          </Col>
          <Col
            sm={2}
            className="d-flex justify-content-end align-items-center mb-1"
          >
            {!isViewOnly && (
              <Link to={"/reminders/maintenancereminders/addnew"}>
                <AddFormButton id="add-reminder-btn">
                  {t("add_reminder")}
                </AddFormButton>
              </Link>
            )}
          </Col>
        </Row>
        <Row>
          <DataReactTable
            reactTableData={reactTableData}
            //valuesArray={fillupsValuesArray}
            initialData={initialData}
            defaultColumns={defaultColumns}
          />
        </Row>
      </Container>
    </>
  );
};

export default MaintenanceRemindersTable;

export const DueComponent = ({
  distance,
  date,
  due_odo,
  due_days,
  rowData,
}) => {
  let distanceUnit = useSelector((state) => state.units.distance);
  if (rowData?.primary_meter === "Hours") {
    distanceUnit = "Hours";
  }
  let distanceText =
    due_odo > 0 && distance > 0
      ? `${formatNumber(distance)} ${getDistanceUnit(distanceUnit)}`
      : NATEXT;
  let dateText =
    due_days > 0 && due_days !== NATEXT && date && date !== NATEXT
      ? date
      : NATEXT;
  //console.log(distance, due_odo, due_days);
  return (
    <div>
      <div>
        {/* {distance} {!distance?.toLocaleString().includes("N/A") ? "Km" : ""}{" "} */}
        {distanceText}
      </div>
      <div>{dateText}</div>
    </div>
  );
};

export const LastServiceComponent = ({ distance, date, rowData }) => {
  let distanceUnit = useSelector((state) => state.units.distance);
  if (rowData?.primary_meter === "Hours") {
    distanceUnit = "Hours";
  }
  let distanceText =
    distance && distance !== NATEXT
      ? `${formatNumber(distance)} ${getDistanceUnit(distanceUnit)}`
      : NATEXT;
  let dateText = date && date !== NATEXT ? date : NATEXT;
  return (
    <div>
      <div>
        {/* {distance} {!distance?.toLocaleString().includes("N/A") ? "Km" : ""}{" "} */}
        {distanceText}
      </div>
      <div>{dateText}</div>
    </div>
  );
};

export const StatusComponent = ({
  due_odo,
  due_days,
  distance,
  percentage,
  days,
  rowData,
}) => {
  const { t } = useTranslation("common");
  let distanceUnit = useSelector((state) => state.units.distance);
  if (rowData?.primary_meter === "Hours") {
    distanceUnit = "Hours";
  }
  //const distanceVal = distance && distance > 0 ? formatNumber(distance, 2) : "";
  let distanceText = "";
  let daysText = "";
  if (percentage >= 100 && (+distance <= 0 || +days <= 0)) {
    distanceText =
      +due_odo > 0 && +distance <= 0
        ? `${t("overdue_by_sm")} ${formatNumber(
            Math.abs(distance),
            2,
          )} ${getDistanceUnit(distanceUnit)} `
        : "";
    daysText =
      +due_days > 0 && +days <= 0
        ? `${t("overdue_by_sm")} ${Math.abs(days)} ${t("days_sm", {
            count: +days,
          })}`
        : "";
  } else if (percentage < 100 && (+distance > 0 || +days > 0)) {
    distanceText =
      +due_odo > 0 && +distance > 0
        ? `${t("due_in_sm")} ${formatNumber(
            Math.abs(distance),
            2,
          )} ${getDistanceUnit(distanceUnit)} `
        : "";
    daysText =
      +due_days > 0 && +days > 0
        ? `${t("due_in_sm")} ${Math.abs(days)} ${t("days_sm", {
            count: +days,
          })}`
        : "";
  }

  // let daysText =
  //   due_days && days
  //     ? `${percentage >= 100 ? t("overdue_by_sm") : t("due_in_sm")} ${Math.abs(
  //         days
  //       )} ${t("days_sm", { count: +days })}`
  //     : "";

  let bottomText = "";
  if (daysText && !distanceText) {
    bottomText = daysText;
  } else if (!daysText && distanceText) {
    bottomText = distanceText;
  } else if (distanceText && daysText) {
    bottomText = `${
      percentage >= 100 ? t("overdue_by_sm") : t("due_in_sm")
    } ${formatNumber(Math.abs(distance), 2)} ${getDistanceUnit(
      distanceUnit,
    )} ${t("or")} ${Math.abs(days)} ${t("days_sm", {
      count: +days,
    })}`;
  }

  let statusText = "SCHEDULED";
  let color = "#5CB85C";
  let background = "";
  if (percentage <= 90) {
    statusText = "SCHEDULED";
    color = "#5CB85C";
  } else if (percentage > 90 && percentage < 100) {
    statusText = "DUE SOON";
    color = "#F0AD4E";
  } else if (percentage >= 100) {
    statusText = "OVERDUE";
    color = "#D9534F";
    //bottomText = "";
  }
  return (
    <div
      className="d-flex flex-column justify-content-center gap-2"
      style={{ paddingRight: "1.25rem" }}
    >
      {/* <div> */}
      {/* <p
        style={{
          color: color,
          //background: "lightgreen",
          width: "80px",
          //textAlign: "center",
          fontWeight: 800,
          fontSize: "12px",
        }}
      >
        {statusText}
      </p> */}

      {/* <div style={{ margin: "0 8px 8px 0" }}> */}
      <ProgressBar color={color} now={percentage} />
      {/* </div> */}

      {/* </div> */}
      {bottomText && <p style={{ fontSize: "11px" }}>{bottomText}</p>}
    </div>
  );
};
